import { Box, SelectChangeEvent } from '@mui/material';
import DateRangeBaseField from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.component';
import {
  DateRangeOption,
  getDateRangeName,
  makeDateRanges,
} from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import SelectBase from 'components/forms/fields/select-field/select-base/select-base.component';
import { DateRangeFilterComponentProps } from 'components/search/search.context';
import { useSearchParam } from 'components/search/search.hooks';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useFocusFilter } from '../search-filters.hooks';
import { useTimeZoneFilter } from './date-filter.hooks';
import { onDateChange } from './date-filter.utils';

type DateFilterProps = {
  startParamName: string;
  endParamName: string;
  predefinedRangeOptions: DateRangeOption[];
  label?: string;
  maxDate?: DateTime;
  anchorDate?: DateTime;
  /** Show only the preset dropdown; hide the custom date-range picker. */
  presetsOnly?: boolean;
};

const DateFilter = ({
  startParamName,
  endParamName,
  predefinedRangeOptions,
  label,
  maxDate,
  anchorDate,
  presetsOnly,
}: DateFilterProps) => {
  const {
    value: startdate,
    setValue: setStartdate,
    setFocus,
    searchParam: {
      filter: { componentProps },
    },
  } = useSearchParam<DateTime>(startParamName);
  const {
    selectBase: selectBaseProps = {},
    dateRangeBaseField: dateRangeBaseFieldProps = {},
  } = (componentProps as DateRangeFilterComponentProps) ?? {};
  const { value: enddate, setValue: setEnddate } =
    useSearchParam<DateTime>(endParamName);
  const dateRanges = useMemo(() => makeDateRanges(anchorDate), [anchorDate]);
  const allowedDateRanges = useMemo(
    () =>
      Object.fromEntries(
        predefinedRangeOptions
          .map(opt => [opt.id, dateRanges[opt.id as keyof typeof dateRanges]])
          .filter(([, v]) => v != null),
      ),
    [predefinedRangeOptions, dateRanges],
  );
  const { ref, open, setOpen } = useFocusFilter(setFocus);

  const onChange = onDateChange(setStartdate, setEnddate);
  useTimeZoneFilter(startParamName, endParamName);

  const onSelectChange = (event: SelectChangeEvent) => {
    if (event.target.value === 'custom') {
      setOpen(true);
    } else {
      const dates = dateRanges[event.target.value];
      setStartdate(dates[0]);
      setEnddate(dates[1]);
    }
  };

  return (
    <Box display="flex">
      <Box display="flex" flex={1}>
        <SelectBase
          value={getDateRangeName([startdate, enddate], allowedDateRanges)}
          options={predefinedRangeOptions}
          onChange={onSelectChange}
          data-testid="DateFilter.Predefined"
          sx={{
            '& fieldset': {
              borderRadius: presetsOnly ? '4px' : '4px 0 0 4px',
            },
          }}
          {
            // In presets-only mode there's no date picker, so the preset dropdown
            // is the only control — wire the focus ref + controlled open state to
            // it so selecting the filter auto-opens it, like the other filters.
            ...(presetsOnly
              ? {
                  ref,
                  SelectProps: {
                    open,
                    onOpen: () => setOpen(true),
                    onClose: () => setOpen(false),
                  },
                }
              : {})
          }
          {...selectBaseProps}
        />
      </Box>
      {!presetsOnly && (
        <Box display="flex" flex={1}>
          <DateRangeBaseField
            value={[startdate, enddate]}
            sx={{
              '& fieldset': {
                borderRadius: '0 4px 4px 0',
              },
            }}
            onChange={onChange}
            dateRangePickerRef={ref}
            open={open && !dateRangeBaseFieldProps.disabled}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            disableFuture
            label={label}
            setOpen={setOpen}
            maxDate={maxDate}
            {...dateRangeBaseFieldProps}
          />
        </Box>
      )}
    </Box>
  );
};

export default DateFilter;
